import React, { useEffect, useState } from "react";
import { Input } from "@vds/inputs";
import { useSelector, useDispatch } from "react-redux";
import {
  FrameForAllFOVs,
  FrameForIMGandInput,
  FrameForImages,
  FrameForLabelAndInput,
  AutoHeightContainer,
} from "../../../../global/components/OnboardingFormStyles/Styles";
import {
  setSelectedCameraName,
  getFovNames,
  getFovIds,
  selectEncodes,
} from "../../../../store/actions";
import { isDuplicateCamName } from "../../../../global/utility/Utils";
import SnapshotComponent from "../../../../pages/CameraOnboarding/SnapshotComponent";
import { Body } from "@vds/typography";
import { DUPLICATE_CAM } from "../../../../pages/CameraOnboarding/cameraOnboardingConstants";
import { Checkbox } from "@vds/checkboxes";
import { Badge } from "@vds/badges";
import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useEnableSearchState } from "../../hooks/useEnableSearchState";
import { useHistoricalSearchEligibility } from "../../hooks/useHistoricalSearchEligibility";

const NameInfoForm = ({ editFlow, onNextDisableChange, onBoradType }) => {
  const dispatch = useDispatch();
  const fieldOfViews =
   useSelector((state) => state.cameraOnboardingReducer.fovIds) || [];
  const { cameraName, camDetails, selectedCamQR, companionDevice, streamInfo } =
    useSelector((state) => state.cameraOnboardingReducer);
  const { fovList, cameraList } = useSelector((state) => state.deviceReducer);
  const { isCameraOffline, effectiveAvailable, notCalibratedFovIds, calibratingFovIds } = useEnableSearchState({
    editFlow,
    cameraId: camDetails?.id || selectedCamQR,
    enabledItems: fieldOfViews,
  });
  const isHistoricalSearchEligible = useHistoricalSearchEligibility(editFlow ? camDetails?.parentdeviceid : companionDevice?.id);

  const [isDuplCamName, setIsDuplCamName] = useState(false);
  const fovId = camDetails?.id || selectedCamQR;
  useEffect(() => {
    if (!fieldOfViews) {
      const fovItems =
        fovList?.filter((item) => item.parentdeviceid === fovId) || [];
      if (fovItems?.length) {
        const defaultFOVNames = fovItems?.map((item) => item?.name);
        dispatch(getFovNames(defaultFOVNames));
      }
    }
    const selCamId = editFlow ? camDetails?.id : selectedCamQR;
    isDuplicateCamName(cameraList, selCamId, cameraName) &&
      setIsDuplCamName(true);
  }, [cameraName]);

  const handleFOVNameChange = (e, index, fovId) => {
    const name = e.target.value;
    // Create a new array instead of mutating the original
    const tempFieldOfViews = fieldOfViews.map((fov, idx) =>
      idx === index ? { ...fov, fovName: name, fovId } : fov
    );
    dispatch(getFovIds(tempFieldOfViews));
  };

  const handleCameraNameChange = (e) => {
    const tempCamName = e.target.value;
    const selCamId = editFlow ? camDetails?.id : selectedCamQR;

    isDuplicateCamName(cameraList, selCamId, tempCamName)
      ? setIsDuplCamName(true)
      : setIsDuplCamName(false);
    dispatch(setSelectedCameraName(tempCamName));
  };
  useEffect(() => {
    const isCameraNameEmpty = !cameraName || cameraName.trim() === "";

    const isAnyFovNameEmpty =
      !fieldOfViews ||
      fieldOfViews?.length === 0 ||
      fieldOfViews?.some((fov) => !fov?.fovName || fov?.fovName === "");

    if (isCameraNameEmpty || isAnyFovNameEmpty || isDuplCamName) {
      onNextDisableChange(true);
    } else {
      onNextDisableChange(false);
    }
  }, [cameraName, fieldOfViews, onNextDisableChange]);

  return (
    <React.Fragment>
      <Body size="large">
        Assign a descriptive name to the camera and each stream.
      </Body>
      <br />

      <AutoHeightContainer style={{ marginBottom: "20px" }}>
        <Input
          label="Camera name"
          width="328px"
          required={true}
          error={isDuplCamName}
          errorText={DUPLICATE_CAM}
          onChange={handleCameraNameChange}
          value={cameraName}
        />
      </AutoHeightContainer>
      <br />
      {fieldOfViews.length > 0 &&
        fieldOfViews.map((i, index) => {
          return (
            <FrameForAllFOVs style={{ marginTop: index > 0 ? "32px" : "0", paddingBottom: "12px", borderBottom: index < fieldOfViews.length - 1 ? "1px solid #e0e0e0" : "none" }}>
              <FrameForIMGandInput>
                <FrameForImages>
                  <SnapshotComponent
                    editFlow={editFlow}
                    fovObj={i}
                    selectedCameraQR={selectedCamQR}
                    companionSelected={companionDevice}
                  />
                </FrameForImages>
                <FrameForLabelAndInput>
                  <Input
                    type="text"
                    required={true}
                    label={`Stream ${index + 1}`}
                    value={i.fovName}
                    onChange={(e) => {
                      handleFOVNameChange(e, index, i.fovId);
                    }}
                    height="64px"
                    width="312px"
                  />
                  {editFlow && isHistoricalSearchEligible && (
                  <div style={{ marginTop: "20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
                      <div style={{ width: "fit-content" }} className="enable-search-checkbox">
                        <Checkbox
                          label="Enable Search"
                          selected={!!i.enableSearch}
                          disabled={isCameraOffline || notCalibratedFovIds.has(i.fovId) || calibratingFovIds.has(i.fovId) || (effectiveAvailable === 0 && !i.enableSearch)}
                          onChange={() => {
                            const newEnableSearch = !i.enableSearch;
                            const updated = fieldOfViews.map((fov, idx) =>
                              idx === index ? { ...fov, enableSearch: newEnableSearch } : fov
                            );
                            dispatch(getFovIds(updated));
                            if (onBoradType === "Manual" && streamInfo?.length) {
                              dispatch(selectEncodes(
                                streamInfo.map((s) =>
                                  s.fovId === i.fovId ? { ...s, enableSearch: newEnableSearch } : s
                                )
                              ));
                            }
                          }}
                        />
                      </div>
                      <Tooltip title="Enabling Search will activate data collection on this stream, allowing you to search footage from this camera." placement="top" PopperProps={{ disablePortal: true }}>
                        <InfoOutlined fontSize="small" style={{ color: "#6f7171", cursor: "pointer", marginLeft: "5px", marginBottom: "4px" }} />
                      </Tooltip>
                    </div>
                    {effectiveAvailable !== null && (
                      <Body size="small" viewport="desktop" primitive="span" color="#6f7171" style={{ marginBottom: "0px", maxWidth: "37.25rem" }}>
                        {effectiveAvailable === 1 ? "1 license is available" : `${effectiveAvailable} licenses are available`}
                      </Body>
                    )}
                    {(isCameraOffline || notCalibratedFovIds.has(i.fovId) || calibratingFovIds.has(i.fovId)) && (
                      <div style={{ marginTop: "8px" }}>
                        <Badge fillColor="yellow">
                          {isCameraOffline
                            ? "Camera is Offline"
                            : notCalibratedFovIds.has(i.fovId)
                            ? "Camera is Not Calibrated"
                            : "Camera is Calibrating"}
                        </Badge>
                      </div>
                    )}
                  </div>
                  )}
                </FrameForLabelAndInput>
              </FrameForIMGandInput>
            </FrameForAllFOVs>
          );
        })}
    </React.Fragment>
  );
};

export default NameInfoForm;
